// Brand tokens — single source of truth for the Sapore di Pane backoffice rebrand.
// Direction: warm artisanal Italian bakery, applied with operational restraint.
// Muted, low-contrast "oatmeal & clay" palette — warmth without loudness, so dense
// tables stay calm and easy on the eyes through long shifts.

export const fontFamilies = {
    display: "'Calistoga', 'Georgia', serif",          // used sparingly: big headings, login, KPI hero
    body: "'Inter', 'Helvetica', 'Arial', sans-serif", // body and all operative UI
    mono: "'JetBrains Mono', 'SFMono-Regular', 'Menlo', monospace", // tabular figures for data
};

const lightBrand = {
    mode: 'light',
    primary: {main: '#8A6650', light: '#A98C72', dark: '#5E4435', contrastText: '#FBF6EE'},
    secondary: {main: '#9C7A4F', light: '#C2A77E', dark: '#6E5436', contrastText: '#FBF6EE'},
    background: {default: '#F6F1E8', paper: '#FDFBF6', chrome: '#ECE3D4'},
    appBar: {bg: '#6E5746', text: '#F3EADD'},
    text: {primary: '#463A30', secondary: '#8A7B6B', onChrome: '#5E4435', onDark: '#FBF6EE'},
    divider: '#E4DAC9',
    field: '#F1EBE0',
    rowHover: '#F1EBE0',
    success: '#5C7A45',
    warning: '#B5852F',
    error: '#A6443A',
    info: '#6B7780',
    plum: '#7B5EA7',
    slateBlue: '#3E6B9C',
    link: '#8A6650',
    agingRamp: ['#5c7a45', '#7a7e3e', '#978136', '#b5852f', '#b06f33', '#ab5a36', '#a6443a', '#6B7C93', '#33465C'],
};

const darkBrand = {
    mode: 'dark',
    // The deliberate distinctive choice: a warm "espresso" dark instead of slate-black,
    // kept muted so the amber accent reads as warmth, not neon.
    primary: {main: '#C99A63', light: '#DCB683', dark: '#9A7444', contrastText: '#241C16'},
    secondary: {main: '#BE9A5E', light: '#D6B884', dark: '#8C6E3F', contrastText: '#241C16'},
    background: {default: '#2B221B', paper: '#362B23', chrome: '#3C3128'},
    appBar: {bg: '#3A2E25', text: '#EDE3D5'},
    text: {primary: '#EDE3D5', secondary: '#B8A48D', onChrome: '#EDE3D5', onDark: '#EDE3D5'},
    divider: '#4A3D32',
    field: '#3C3128',
    rowHover: '#423529',
    success: '#86B26A',
    warning: '#D2A85A',
    error: '#D98A80',
    info: '#93A4AC',
    plum: '#B39BD0',
    slateBlue: '#8FB0D3',
    link: '#C99A63',
    agingRamp: ['#86b26a', '#9faf65', '#b9ab5f', '#d2a85a', '#d49e67', '#d79473', '#d98a80', '#9FB3C8', '#7C9AB8'],
};

export const brandTokens = {light: lightBrand, dark: darkBrand};

// The single filled action button. Mode-independent on purpose: the primary action is
// always "crust brown with cream text" in both light and dark, so it reads as one
// consistent brand element (and cream stays legible on it in either mode).
export const actionButton = {bg: '#8A6650', hover: '#74543F', text: '#FBF6EE'};

// Categorical chart palette. Earthy and muted to stay on-brand, but spread across
// distinct hues (brown, teal, gold, plum, olive, brick, slate, tan...) and ordered so
// adjacent series contrast strongly — categorical data needs separability, not a
// single warm ramp where neighbouring bars blur together.
export const chartPalette = [
    '#8A6650', // clay brown
    '#4E8C8A', // teal
    '#C99A3F', // gold / mustard
    '#7B5EA7', // muted plum
    '#5C7A45', // olive green
    '#B5563C', // terracotta
    '#3E6B9C', // slate blue
    '#C2A77E', // sand
    '#A23E5C', // wine
    '#6B8E3D', // leaf green
    '#9C7A4F', // bronze
    '#D08A3E', // amber
];
